import React, {
  useState,
  useContext,
  useEffect,
  Fragment,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { addPolicy, editPolicy } from "../helper";
import PolicyContext from "../../../context/policy/PolicyContext";
import RichTextBoxEditer from "../../../components/RichTextBoxEditer";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import { convertToUTC, isClientAdmin } from "../../../utils/ConvertDate";
import { useUserContext } from "../../../context/auth/UserContext";
import AutoVersionIncrement from "../../../components/AutoVersionIncrement";
import { useLocation } from "react-router-dom";
import { useAccessRights } from "../../../utils/useAccessRights";

export const PolicyForm = () => {
  const navigate = useNavigate();
  const policyPageAccess = useAccessRights("Policy Page");

  const location = useLocation();
  const { userId, companyProfileID, userAccess } = useUserContext();
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const isUpdatedRef = useRef(isUpdated);
  const [policyFormTitle, setPolicyFormTitle] =
    useState<string>("Create Policy");
  const [policyName, setPolicyName] = useState<string>("");
  const [policyDetails, setPolicyDetails] = useState<string>("");
  const [policyVersion, setPolicyVersion] = useState<string>("");
  const [policyOriginalVersion, setPolicyOriginalVersion] =
    useState<string>("");
  const [policyVersionIncrement, setPolicyVersionIncrement] =
    useState<string>("");
  const [policyStatus, setPolicyStatus] = useState<string>("");
  const [policyApprovalDate, setPolicyApprovalDate] = useState<Dayjs | null>(
    null
  );
  const [isUserStillOnPage, setIsUserStillOnPage] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDeleteConfirmationDialog: false,
    openEditConfirmationDialog: false,
    openSuccessDialogBox: false,
  });

  const policyContext = useContext(PolicyContext);
  const { previewPolicy } = policyContext;

  const displayPolicyList = async () => {
    navigate("/policies");
  };

  const handleCloseDialog = () => {
    if (dialogState.title !== "Error") {
      setDialogState({
        ...dialogState,
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: false,
      });
      navigate("/policies");
    } else {
      setDialogState({
        ...dialogState,
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: false,
      });
    }
  };

  const handleCloseEditDialog = () => {
    setPolicyStatus("Approved");
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
      openEditConfirmationDialog: false,
    });
  };

  const disableVersionChange = () => {
    if (
      previewPolicy?.policyID &&
      previewPolicy.policyStatus !== policyStatus &&
      policyStatus === "Approved"
    ) {
      return false;
    }
    return true;
  };

  const disableForm = () => {
    if (
      policyName !== "" &&
      policyDetails !== "" &&
      policyVersion !== "" &&
      policyStatus !== ""
    ) {
      if (policyStatus === "Approved") {
        if (
          policyApprovalDate === null ||
          (!disableVersionChange() && policyVersionIncrement === "")
        ) {
          return true;
        }
      }
      return false;
    } else {
      return true;
    }
  };

  const handleAddPolicy = async () => {
    const date = new Date();
    const payload = {
      policyName: policyName,
      policyContent: policyDetails,
      policyVersion: policyVersion,
      policyStatus: policyStatus,
      policyApprovalDate: convertToUTC(policyApprovalDate),
      PolicyApprovedBy: policyStatus === "Approved" ? userId : 0,
      isTemplateImport: false,
      companyProfileID: companyProfileID,
      isInclude: true,
      createdBy: userId,
      createDate: date,
      isEditedByUserId: "0",
    };
    const res = await addPolicy(payload);
    // console.log("handleAddPolicy res", payload, res);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Policy successfully created",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const handleEditPolicy = async () => {
    setIsUpdated(true);
    const date = new Date();
    const payload = {
      policyName: policyName,
      policyContent: policyDetails,
      policyVersion: policyVersion,
      policyStatus: policyStatus,
      policyApprovalDate: convertToUTC(policyApprovalDate),
      PolicyApprovedBy:
        policyStatus !== previewPolicy.policyStatus &&
        policyStatus === "Approved"
          ? userId
          : 0,
      isTemplateImport: true,
      isInclude: true,
      policyID: previewPolicy.policyID,
      companyProfileID: companyProfileID,
      updateBy: userId,
      updateDate: date,
      isEditedByUser: false,
      isEditedByUserId: "0",
    };

    const res = await editPolicy(payload);
    // console.log("handleEditPolicy res", payload, res);
    if (res.succeeded === true) {
      setDialogState({
        title: "Success",
        body: "Policy successfully updated",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openEditConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const handleEdit = () => {
    if (
      previewPolicy.policyStatus === "Approved" &&
      previewPolicy.policyStatus === policyStatus
    ) {
      setDialogState({
        title: "Confirmation",
        body: "Editing an Approved record will change the status to Ready to Approve . Do you want to continue?",
        action: "OK",
        actionCancel: "Cancel",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: false,
        openEditConfirmationDialog: true,
      });
      setPolicyStatus("Ready to Approve");
    } else {
      handleEditPolicy();
    }
  };

  useEffect(() => {
    if (policyStatus !== "Approved") {
      setPolicyVersionIncrement("");
      setPolicyVersion(policyOriginalVersion);
      setPolicyApprovalDate(null);
    }
  }, [policyStatus]);

  useEffect(() => {
    if (previewPolicy && Object.keys(previewPolicy).length > 0) {
      const approvalDate =
        previewPolicy.policyApprovalDate !== null
          ? dayjs(previewPolicy.policyApprovalDate)
          : null;

      setPolicyName(previewPolicy.policyName);
      setPolicyDetails(previewPolicy.policyContent);
      setPolicyVersion(previewPolicy.policyVersion);
      setPolicyStatus(previewPolicy.policyStatus);
      setPolicyOriginalVersion(previewPolicy.policyVersion);
      setPolicyVersionIncrement("");
      setPolicyApprovalDate(approvalDate);
      setPolicyFormTitle("Edit Policy");
    } else {
      setPolicyName("");
      setPolicyDetails("");
      setPolicyVersion("1.0");
      setPolicyOriginalVersion("1.0");
      setPolicyVersionIncrement("");
      setPolicyStatus("Not Approved");
      setPolicyApprovalDate(null);
      setPolicyFormTitle("Create Policy");
    }
  }, [previewPolicy]);

  useEffect(() => {
    console.log("isUpdated", isUpdated);
    isUpdatedRef.current = isUpdated;
  }, [isUpdated]);

  const handleExit = async (isEdited: boolean) => {
    console.log("handleExit", isUpdatedRef.current);
    if (previewPolicy?.policyID && !isUpdatedRef.current) {
      const date = new Date();
      const payload = {
        ...previewPolicy,
        isEditedByUser: isEdited,
        isEditedByUserId: userId + "",
        updateBy: userId,
        updateDate: date,
      };
      try {
        const resUpdate = await editPolicy(payload);
        console.log("handleExit resUpdate", resUpdate);
        if (resUpdate) {
          setIsUserStillOnPage(false);
          return resUpdate;
        }
      } catch (error) {
        console.log("@@Error", error);
      }
    }
  };

  useEffect(() => {
    return () => {
      handleExit(false);
    };
  }, [location]);

  useEffect(() => {
    console.log("isUserStillOnPage >> ", isUserStillOnPage);
    if (isUserStillOnPage) {
      handleExit(true);
    }
  }, [isUserStillOnPage]);

  useEffect(() => {
    console.log("beforeunload");

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      console.log("handleBeforeUnload", event);
      event.preventDefault();
      await handleExit(false);
      setIsUserStillOnPage(true);
      const message =
        "You have unsaved changes. Are you sure you want to leave? ";
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Fragment>
      <Grid
        container
        justifyContent="center"
        sx={{
          height: "100%",
          width: "100%",
          padding: "1rem",
          margin: "1rem 0",
          background: "#FFF",
          borderRadius: "2%",
        }}
      >
        <Grid xs={12}>
          <h3 style={{ fontWeight: "400", margin: "0.5rem 0" }}>
            {policyFormTitle}
          </h3>
        </Grid>

        <Grid container spacing={2} sx={{ height: "100%", width: "70%" }}>
          <Grid xs={12}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Policy Name
            </h4>
            <TextField
              fullWidth
              variant="filled"
              size="small"
              value={policyName}
              onChange={(e) => setPolicyName(e.target.value)}
            />
          </Grid>
          <Grid xs={12}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Policy Details
            </h4>
            <RichTextBoxEditer
              readOnly={false}
              editorContent={policyDetails}
              onChange={(val) => setPolicyDetails(val)}
            />
          </Grid>
          <AutoVersionIncrement
            disableIncrement={disableVersionChange()}
            version={policyVersion}
            originalVersion={policyOriginalVersion}
            versionIncrement={policyVersionIncrement}
            onChange={(version, versionIncrement) => {
              setPolicyVersion(version);
              setPolicyVersionIncrement(versionIncrement);
            }}
          />
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Policy Status
            </h4>
            <FormControl variant="filled" sx={{ minWidth: 200, width: "100%" }}>
              <Select
                value={policyStatus}
                onChange={(e) => setPolicyStatus(e.target.value)}
              >
                <MenuItem value="Not Approved">Not Approved</MenuItem>
                <MenuItem value="Ready to Approve">Ready to Approve</MenuItem>
                <MenuItem
                  value="Approved"
                  disabled={!policyPageAccess?.isApproved}
                >
                  Approved
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={6}>
            <h4
              style={{
                color: "lightgrey",
                fontWeight: "400",
                fontStyle: "italic",
                margin: "0.5rem 0",
              }}
            >
              Policy Approval Date
            </h4>
            <DatePicker
              value={policyApprovalDate}
              onChange={(val) => setPolicyApprovalDate(val)}
              slotProps={{ field: { clearable: true } }}
            />
          </Grid>
          <Grid xs={12} container justifyContent="end">
            <Button
              sx={{ p: 1, m: 1 }}
              variant="outlined"
              size="small"
              onClick={displayPolicyList}
            >
              Cancel
            </Button>
            {policyFormTitle === "Edit Policy" ? (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                onClick={handleEdit}
                disabled={disableForm() || !policyPageAccess?.isUpdate}
              >
                Edit
              </Button>
            ) : (
              <Button
                sx={{ p: 1, m: 1 }}
                variant="contained"
                size="small"
                onClick={handleAddPolicy}
                disabled={disableForm() || !policyPageAccess?.isCreate}
              >
                Create
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      {
        // Edit confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openEditConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleEditPolicy}
          handleCancel={handleCloseEditDialog}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
